import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ChunkedTokenStorage } from '../utils/helpers/chunked-token-storage';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { from, Observable, throwError } from 'rxjs';
import { safeGetItem } from '../utils/storage.util';
import { getCookie } from '../utils/cookie.util';
import { ToasterService } from '../services/ui-services.ts/toaster.service';
import { AuthService } from '../auth/auth.service';

/**
 * Interceptor to attach JWT tokens to outgoing requests and handle 401 errors.
 */
export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const chunkedStorage = inject(ChunkedTokenStorage);
  const authService = inject(AuthService);
  const toastr = inject(ToasterService);
  const translate = inject(TranslateService);
  const router = inject(Router);

  // Get current language for headers
  const lang = (safeGetItem('language') as string) || getCookie('language') || 'ar';

  // Skip token handling for refresh token requests to avoid infinite loops
  if (req.url.includes('auth/refresh-token')) {
    return next(req);
  }

  /**
   * Helper to handle unauthorized access
   */
  const handleUnauthorized = (): Observable<any> => {
    authService.logout();
    toastr.error(translate.instant('ErrorInterceptor.Unauthenticated'));
    
    return throwError(() => new HttpErrorResponse({
      status: 401,
      statusText: 'Unauthorized',
      error: 'Session expired'
    }));
  };

  /**
   * Helper to attempt token refresh
   */
  const handleTokenRefresh = (): Observable<any> => {
    return authService.refreshToken().pipe(
      switchMap(success => {
        if (success) {
          const newToken = chunkedStorage.getToken('accessToken');
          if (!newToken) return handleUnauthorized();

          const authReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
              PreferredLanguage: lang,
              'Accept-Language': lang,
            },
          });
          return next(authReq);
        }
        return handleUnauthorized();
      }),
      catchError(error => {
        toastr.error(error?.error?.message || error?.message || 'Session expired');
        return handleUnauthorized();
      })
    );
  };

  const token = chunkedStorage.getToken('accessToken');

  // 1. No token -> send request without Authorization header
  if (!token) {
    const publicReq = req.clone({
      setHeaders: {
        PreferredLanguage: lang,
        'Accept-Language': lang,
      },
    });
    return next(publicReq);
  }

  // 2. Proactive Refresh -> if token is expiring soon, refresh before sending
  if (authService.isTokenExpiringSoon()) {
    return from(authService.refreshToken()).pipe(
      switchMap(success => {
        const activeToken = success ? chunkedStorage.getToken('accessToken') : token;
        const authReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${activeToken}`,
            PreferredLanguage: lang,
            'Accept-Language': lang,
          },
        });
        return next(authReq);
      }),
      catchError(() => {
        // If proactive refresh fails, attempt with current token and let 401 handler deal with it
        const authReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            PreferredLanguage: lang,
            'Accept-Language': lang,
          },
        });
        return next(authReq);
      })
    );
  }

  // 3. Normal Authenticated Request
  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      PreferredLanguage: lang,
      'Accept-Language': lang,
    },
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // 4. Reactive Refresh -> handle 401 by attempting refresh
      if (error.status === 401) {
        return handleTokenRefresh();
      }
      return throwError(() => error);
    })
  );
};
